import {
  VerticalStackedBarChart as FluentVerticalStackedBarChart,
  type VerticalStackedChartProps,
} from '@fluentui/react-charts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { seriesColor } from './colors';
import { formatValue, tickFormatter } from './format';
import type { ChartUnit, StackedBarPoint } from './types';

interface StackedBarChartProps {
  bars: readonly StackedBarPoint[];
  unit?: ChartUnit;
  height?: number;
  chartTitle?: string;
  /** Hide the per-segment legend rail. Defaults to false so stacks self-label. */
  hideLegend?: boolean;
}

const useStyles = makeStyles({
  wrapper: {
    width: '100%',
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Stacked vertical bar chart — one bar per category, segments stacked from
 * bottom to top in the order given. Used for breakdowns where the total
 * matters as much as the components (e.g. Token $ + DI $ → Total $).
 *
 * Segment colors fall back to the design-system palette in declaration order
 * when a segment doesn't provide its own.
 */
export function StackedBarChart({
  bars,
  unit,
  height = 260,
  chartTitle,
  hideLegend = false,
}: StackedBarChartProps) {
  const classes = useStyles();

  const data: VerticalStackedChartProps[] = useMemo(
    () =>
      bars.map((bar) => ({
        xAxisPoint: bar.x,
        chartData: bar.segments.map((seg, i) => ({
          legend: seg.name,
          data: seg.value,
          color: seg.color ?? seriesColor(i),
          // Match BarChart / DonutChart: the formatted value lives on the
          // y-axis callout, which is what Fluent Charts renders as the
          // value line in tooltips.
          yAxisCalloutData: formatValue(seg.value, unit),
        })),
      })),
    [bars, unit],
  );

  return (
    <div className={classes.wrapper}>
      <FluentVerticalStackedBarChart
        data={data}
        height={height}
        chartTitle={chartTitle}
        yAxisTickFormat={tickFormatter(unit)}
        hideLegend={hideLegend}
        reflowProps={{ mode: 'min-width' }}
      />
    </div>
  );
}
